/**
 * kpiSortSession.ts
 *
 * Session-scoped persistence for the KPI card sort mode toggle.
 *
 * - Web:          uses sessionStorage (cleared when the browser tab/window closes)
 * - React Native: uses an in-memory module-level variable (cleared when the app process restarts)
 *
 * Neither mechanism persists across app/browser launches, satisfying the
 * requirement that the default resets on each new session.
 */

import { Platform } from 'react-native';

export type KpiSortMode = 'BIGGEST_DELTA' | 'SPARC_CATEGORY';

const SESSION_KEY = 'kpi_sort_mode';
const DEFAULT_MODE: KpiSortMode = 'SPARC_CATEGORY';

// ─── React Native in-memory store ────────────────────────────────────────────
// Module-level variable: survives navigation (module is kept alive) but is
// reset whenever the JS runtime restarts (i.e. app is killed/restarted).
let _nativeSessionValue: KpiSortMode = DEFAULT_MODE;

// ─── Public API ───────────────────────────────────────────────────────────────

export function getKpiSortMode(): KpiSortMode {
	if (Platform.OS === 'web') {
		try {
			const stored = sessionStorage.getItem(SESSION_KEY);
			if (stored === 'BIGGEST_DELTA' || stored === 'SPARC_CATEGORY') {
				return stored;
			}
		} catch {
			// sessionStorage unavailable (e.g. private-browsing restrictions) — fall through
		}
		return DEFAULT_MODE;
	}

	// React Native: in-memory
	return _nativeSessionValue;
}

export function setKpiSortMode(mode: KpiSortMode): void {
	if (Platform.OS === 'web') {
		try {
			sessionStorage.setItem(SESSION_KEY, mode);
		} catch {
			// Silently ignore storage errors
		}
		return;
	}

	// React Native: in-memory
	_nativeSessionValue = mode;
}
